import t from 'react-translate';
import { useSelector } from 'react-redux';
import { MentoringProgramEnrollment } from 'redux/schemas/models/mentoring-program-enrollments';
import { getMentoringProgramEnrollment } from 'redux/selectors/mentoring-program-enrollments';
import { ProgramLevelQuestionType } from 'redux/schemas/models/mentoring-program-profile-questions';
import { DEFAULT_CONCURRENT_MENTOR_CONNECTION } from 'athena/components/mentoring-program/constants';
import { config } from '@config/pendo.config.json';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { useContext, useEffect, useState } from 'react';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import Question from '../../question';
import NvPopover from 'shared/components/nv-popover';
import NvConfirmationPopover from 'shared/components/nv-confirmation-popover';
import { useFormContext } from 'react-hook-form';
import useMentorAvailability from 'athena/hooks/use-mentor-availability';
import { MentoringProgramMatchingType } from 'redux/schemas/models/mentoring-programs';
import { css } from '@emotion/react';
import { doubleModalZIndex } from 'shared/components/nv-modal';

type MentorCapacityQuestionProps = {
  enrollment: MentoringProgramEnrollment;
  maxMentees: number;
};

export const overlayStyles = css`
  width: 328px;
  max-width: 328px;
  z-index: ${doubleModalZIndex};

  .confirmation {
    width: 100% !important;
    max-width: 100% !important;

    .button-container {
      justify-content: center !important;
    }
  }
`;

const CONCURRENT_MENTOR_CONNECTION_ID = 'concurrent_mentor_connection_setting';

const MentorCapacityQuestion = ({
  enrollment,
  maxMentees,
}: MentorCapacityQuestionProps) => {
  const { id: enrollmentId } = enrollment;
  const mentorshipProgramEnrollment: MentoringProgramEnrollment = useSelector(
    (state) => getMentoringProgramEnrollment(state, enrollmentId)
  );
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { id: mentorshipProgramId, matchingType } = mentoringProgram;
  const mentoringProgramAliases = useSelector((state) =>
    getMentoringProgramAliases(state, mentorshipProgramId)
  );
  const { concurrentMentorConnection } = mentorshipProgramEnrollment || {};
  const translationBase =
    t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.PROFILE_MODAL
      .PROGRAM_QUESTIONS.MENTOR_CAPACITY;
  const [showConfirmation, setShowConfirmation] = useState(false);

  const concurrentMenteesOptions = Array.from({ length: maxMentees }, (_, i) =>
    (i + 1).toString()
  );

  const dataQATag =
    config.pendo.athena.mentorshipProgram.participantHome.steps.profile
      .questions;

  const label = translationBase.TITLE(mentoringProgramAliases);
  const { currentCapacity } = useMentorAvailability(enrollment);
  const { watch, formState: { touchedFields } } = useFormContext();
  const { answer: selectedCapacity } = watch(CONCURRENT_MENTOR_CONNECTION_ID) ?? {};
  const isAtCapacityMessage =
    Number(selectedCapacity) === currentCapacity &&
    translationBase.WARNING.CONTENT_AT_CAPACITY();
  const isOverCapacityMessage =
    Number(selectedCapacity) < currentCapacity &&
    translationBase.WARNING.CONTENT_OVER_CAPACITY();
  const isParticipantDrivenMatching =
    matchingType === MentoringProgramMatchingType.PARTICIPANT_DRIVEN;
  const warningMessage =
    isParticipantDrivenMatching &&
    (isOverCapacityMessage || isAtCapacityMessage);

  useEffect(() => {
    const isTouched = touchedFields[CONCURRENT_MENTOR_CONNECTION_ID];
    setShowConfirmation(isTouched && !!warningMessage);
  }, [warningMessage, touchedFields]);

  return (
    <NvPopover
      enabled
      show={showConfirmation}
      overlayStyles={overlayStyles}
      content={
        <NvConfirmationPopover
          onConfirm={() => setShowConfirmation(false)}
          header={translationBase.WARNING.TITLE()}
          message={warningMessage || ''}
          confirmBtnText={t.FORM.OK()}
          pendoTagName={dataQATag.mentorCapacityWarningConfirm}
        />
      }
      placement='left'
      rootClose
      onHide={() => setShowConfirmation(false)}
      className='d-inline-flex mw-100'
      preventOverflow
    >
      <div className='w-100'>
        <Question
          isRequired
          id={CONCURRENT_MENTOR_CONNECTION_ID}
          type={ProgramLevelQuestionType.DROPDOWN}
          title={label}
          answer={`${
            concurrentMentorConnection || DEFAULT_CONCURRENT_MENTOR_CONNECTION
          }`}
          options={concurrentMenteesOptions}
          dataQa={dataQATag.mentorCapacity}
          dataQaId={`${dataQATag.mentorCapacity}-${enrollmentId}`}
          ariaLabel={label}
          renderFallbackValue
        />
        <div className='text-small semi-bold gray-2'>
          {translationBase.NOTE()}
        </div>
      </div>
    </NvPopover>
  );
};

export default MentorCapacityQuestion;
